// src/features/admin/components/DashboardOverview.jsx
import { useEffect, useState } from "react";
import { School, Users, GraduationCap, ClipboardList } from "lucide-react";
import { BASE_URL } from "shared/lib/config";

export default function DashboardOverview() {
  const [stats, setStats] = useState({
    schools: 0,
    users: 0,
    classes: 0,
    assessments: 0,
    asOf: null,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Helper to mirror Adminhome logic: prefer sessionStorage if it has idToken
  const getStore = () => {
    if (typeof window === "undefined") return null;
    if (sessionStorage.getItem("idToken")) return sessionStorage;
    if (localStorage.getItem("idToken")) return localStorage;
    return null;
  };

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setLoading(true);
        setError("");

        const store = getStore();
        const token = store?.getItem("idToken");

        if (!token) {
          throw new Error("Tidak menemukan token login. Silakan login kembali.");
        }

        const res = await fetch(`${BASE_URL}/api/overview`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          throw new Error("Gagal memuat data overview (status " + res.status + ")");
        }

        const data = await res.json();

        setStats({
          schools: data.totalSchools ?? 0,
          users: data.totalUsers ?? 0,
          classes: data.totalClasses ?? 0,
          assessments: data.totalAssessments ?? 0,
          asOf: data.asOf ? new Date(data.asOf) : null,
        });
      } catch (err) {
        console.error("[DashboardOverview] fetch error:", err);
        setError(err.message || "Terjadi kesalahan saat memuat data overview.");
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, []);

  const cards = [
    {
      title: "Total Sekolah",
      value: stats.schools,
      icon: School,
      lightColor: "bg-blue-50",
      textColor: "text-blue-600",
    },
    {
      title: "Total Pengguna",
      value: stats.users,
      icon: Users,
      lightColor: "bg-green-50",
      textColor: "text-green-600",
    },
    {
      title: "Total Kelas",
      value: stats.classes,
      icon: GraduationCap,
      lightColor: "bg-orange-50",
      textColor: "text-orange-600",
    },
    {
      title: "Total Asesmen",
      value: stats.assessments,
      icon: ClipboardList,
      lightColor: "bg-purple-50",
      textColor: "text-purple-600",
    },
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="text-sm text-gray-500">
          Memuat ringkasan dashboard…
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
            <p className="text-gray-600 mt-1">Ringkasan data sistem Potentia</p>
          </div>
          {stats.asOf && (
            <p className="text-xs text-gray-500">
              Terakhir diperbarui:{" "}
              {stats.asOf.toLocaleString("id-ID", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          )}
        </div>
        {error && (
          <p className="mt-2 text-xs text-red-500">
            {error}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md hover:border-gray-300 transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <div
                  className={`w-12 h-12 ${card.lightColor} rounded-xl flex items-center justify-center shadow-sm`}
                >
                  <Icon className={`w-6 h-6 ${card.textColor}`} />
                </div>
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">
                {card.title}
              </h3>
              <p className="text-3xl font-bold text-gray-900">
                {card.value.toLocaleString("id-ID")}
              </p>
            </div>
          );
        })}
      </div>

      <div className="bg-gradient-to-br from-[#4F7CFF] to-[#3D6AEE] rounded-xl p-8 text-white shadow-lg shadow-blue-500/20">
        <h3 className="text-2xl font-bold mb-2">
          Selamat Datang di Admin Dashboard
        </h3>
        <p className="text-blue-100 max-w-2xl">
          Kelola seluruh data sekolah, pengguna, kelas, dan hasil asesmen
          dalam satu platform. Gunakan menu navigasi di atas untuk mengakses
          berbagai fitur manajemen data.
        </p>
      </div>
    </div>
  );
}
